import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { RecoveryAccountHeader } from "../../components/RecoveryAccountHeader/RecoveryAccountHeader";
import Button, { ButtonColors, ButtonTypes } from "../../components/Button";
import { SessionService } from "../../services/SessionService";
import { toast } from "react-toastify";
import { UserService } from "../../services/UsersService";
import { setAuthUser } from "../../redux/authentication/auth-slice";
import { FaArrowLeft } from "react-icons/fa";
import {
  clearPasswordResetSentContext,
  hydratePasswordResetSentFromSessionOrLegacy,
  type PasswordResetSentStorage,
} from "../../utils/recoveryFlowStorage";

const RESEND_COOLDOWN_SECONDS = 30;

export default function PasswordResetLinkSent() {
  const { pool_id: poolId, pool_tenant: poolTenant } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [sentContext] = useState<PasswordResetSentStorage | null>(() =>
    hydratePasswordResetSentFromSessionOrLegacy(),
  );

  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasLegacy = params.has("email") || params.has("response_type");

    params.delete("email");
    params.delete("response_type");

    if (!hasLegacy) return;

    const q = params.toString();
    navigate(
      { pathname: location.pathname, search: q ? `?${q}` : "" },
      { replace: true },
    );
  }, [location.search, location.pathname, navigate]);

  const contextValid = Boolean(poolId && poolTenant && sentContext);

  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [isLoadingResend, setIsLoadingResend] = useState(false);

  const intervalRef = useRef<number | null>(null);

  const clearCooldown = useCallback(() => {
    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startCooldown = useCallback(() => {
    clearCooldown();
    setResendDisabled(true);
    setCountdownSeconds(RESEND_COOLDOWN_SECONDS);
    intervalRef.current = window.setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev <= 1) {
          clearCooldown();
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearCooldown]);

  useEffect(() => clearCooldown, [clearCooldown]);

  useEffect(() => {
    void SessionService.clearLocalSession().finally(() => {
      dispatch(setAuthUser(null));
    });
  }, [dispatch]);

  useEffect(() => {
    if (!poolId || !poolTenant) return;
    if (!contextValid) {
      toast.info(
        "Para reenviar o link, use novamente o fluxo de alteração de senha na segurança da conta.",
      );
      clearPasswordResetSentContext();
      navigate(`/${poolId}/${poolTenant}`, { replace: true });
    }
  }, [poolId, poolTenant, contextValid, navigate]);

  useEffect(() => {
    if (!contextValid) return undefined;
    startCooldown();
    return () => clearCooldown();
  }, [contextValid, startCooldown, clearCooldown]);

  const handleResend = async () => {
    if (
      !contextValid ||
      !poolId ||
      !sentContext ||
      resendDisabled ||
      isLoadingResend
    ) {
      return;
    }

    setIsLoadingResend(true);
    try {
      await UserService.sendPasswordRecoveryLink(
        poolId,
        sentContext.email,
        sentContext.response_type,
      );
      toast.success("E-mail reenviado.");
      startCooldown();
    } catch {
      toast.error(
        "Não foi possível reenviar o e-mail com o link. Tente novamente.",
      );
    } finally {
      setIsLoadingResend(false);
    }
  };

  const loginPath = poolId && poolTenant ? `/${poolId}/${poolTenant}` : "/";

  if (!poolId || !poolTenant || !sentContext) {
    return null;
  }

  return (
    <div className="min-vh-100 d-flex flex-column">
      <RecoveryAccountHeader title="E-mail enviado" />
      <Container className="flex-grow-1 d-flex py-5">
        <Row className="justify-content-center w-100 mx-0 align-self-center">
          <Col md={10} lg={7} xl={6}>
            <Card className="shadow-sm rounded-5">
              <Card.Body className="p-md-5 p-4">
                <p className="mb-4 text-muted mb-md-5">
                  Enviamos um e-mail com o link para redefinir sua senha, se esse
                  endereço estiver cadastrado. Confira a caixa de entrada e o spam.
                  Depois de definir uma nova senha pelo link, faça login de novo.
                </p>

                <div className="d-flex flex-column align-items-center gap-3 mb-4">
                  <Button
                    text={"Reenviar e-mail"}
                    color={ButtonColors.primary}
                    isLoading={isLoadingResend}
                    disabled={resendDisabled || isLoadingResend}
                    onClick={handleResend}
                    type={ButtonTypes.button}
                  />
                  {resendDisabled && countdownSeconds > 0 && (
                    <p className="text-muted small mb-0 text-center" aria-live="polite">
                      Você pode pedir novo e-mail em {countdownSeconds}{" "}
                      {countdownSeconds === 1 ? "segundo" : "segundos"}.
                    </p>
                  )}
                </div>

                <div className="d-flex justify-content-start mt-5">
                  <Link
                    to={loginPath}
                    className="d-inline-flex align-items-center text-start text-decoration-none link-primary p-0"
                    style={{ gap: "0.35rem" }}
                  >
                    <FaArrowLeft className="flex-shrink-0" aria-hidden />
                    <span>Voltar à página de login</span>
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
